import { Material } from '../../../../sp-types';
import { IClientExposure, IFutureOrder, ISupplierExposure } from '../../../../sp-types/FutureOrderType';
import { Product } from '../../../../sp-types/productType';
import { MonthYearValue } from '../../../../utils/MonthYear/MonthYearValue';
import { MonthYear, monthYearToString, sortMonthYear } from '../../../../utils/MonthYear/monthYear';
import { updateFutureOrderValueCalendars } from '../AddNewFutureOrderUtils';
import { makeNegative } from '../FOIndexedClientExposure/FillOrderAdjustmentsUtils';

export const rawMaterialsToMap = (rawMaterials: ISupplierExposure[]): Record<string, ISupplierExposure> => {
  return rawMaterials.reduce((acc, itm) => ({ ...acc, [itm.materialId]: itm }), {});
};

const monthValueToObject = (
  monthValue: MonthYearValue[],
  hashFunc: (_month: MonthYear) => string,
): Record<string, number> => monthValue.reduce((acc, { date, value }) => ({ ...acc, [hashFunc(date)]: value }), {});

export const calcSupplierMinusClient = (
  supplierExposure: ISupplierExposure[],
  clientExposure: Record<string, IClientExposure>,
): Record<string, MonthYearValue[]> => {
  const result = supplierExposure.reduce((acc, { materialId, exposureCalendar }) => {
    const clientCalendar = clientExposure[materialId].exposureCalendar;
    const supplierCalendar = exposureCalendar;

    const supplierMonthYearMap = monthValueToObject(supplierCalendar, monthYearToString);
    const clientMonthYearMap = monthValueToObject(clientCalendar, monthYearToString);

    const resultArr = clientCalendar.map(({ date, value: clientValue }) => {
      const supplierValue = supplierMonthYearMap[monthYearToString(date)] || 0;
      return { date, value: supplierValue - clientValue };
    });

    const monthsOnlyInSupplier = supplierCalendar.filter(({ date }) => !clientMonthYearMap[monthYearToString(date)]);

    acc[materialId] = [...resultArr, ...monthsOnlyInSupplier];
    return acc;
  }, {} as Record<string, MonthYearValue[]>);

  const clientOnlyMaterials = Object.entries(clientExposure)
    .filter(([materialId]) => !result[materialId])
    .reduce(
      (acc, [materialId, { exposureCalendar }]) => ({ ...acc, [materialId]: makeNegative(exposureCalendar) }),
      {},
    );

  return { ...clientOnlyMaterials, ...result };
};

const getMonthDelta = (startDate: MonthYear, endDate: MonthYear): number =>
  12 * (endDate.year - startDate.year) + endDate.month - startDate.month + 1;

const getMonthsRange = (monthCount: number, startingDate: MonthYear): MonthYear[] =>
  Array.from({ length: monthCount }).map((_, i) => ({
    year: startingDate.year + Math.floor((startingDate.month + i - 1) / 12),
    month: ((startingDate.month + i - 1) % 12) + 1,
  }));

export const accumulateExposures = (
  netExposure: Record<string, MonthYearValue[]>,
): Record<string, MonthYearValue[]> => {
  const result = Object.entries(netExposure).reduce((acc, [materialId, values]) => {
    const sortedValues = values.sort((a, b) => sortMonthYear(a.date, b.date));
    const startingDate = sortedValues[0].date;
    const lastMonth = sortedValues[sortedValues.length - 1].date;
    const monthCount = getMonthDelta(startingDate, lastMonth);

    const monthValues = monthValueToObject(sortedValues, monthYearToString);

    let runningTotal = 0;
    const allMonths = getMonthsRange(monthCount, startingDate);
    const accumulatedValues = allMonths.map((date) => {
      runningTotal += monthValues[monthYearToString(date)] || 0;
      return { date, value: runningTotal };
    });

    acc[materialId] = accumulatedValues.map((item) => ({ ...item, value: -item.value }));
    return acc;
  }, {} as Record<string, MonthYearValue[]>);

  return result;
};

export const isSupplierAdjustmentMultiValue = (supplierInfo: ISupplierExposure[]): boolean => {
  if (supplierInfo.length === 0) return false;
  const firstAdjustment = supplierInfo[0].adjustment;
  return supplierInfo.some((supplier) => supplier.adjustment !== firstAdjustment);
};

export const isSupplierLeadTimeMultiValue = (supplierInfo: ISupplierExposure[]): boolean => {
  if (supplierInfo.length === 0) return false;
  const firstLeadTime = supplierInfo[0].supplyLeadTime;
  return supplierInfo.some((supplier) => supplier.supplyLeadTime !== firstLeadTime);
};

export const updateEditedFutureOrderCharts = (
  futureOrder: IFutureOrder,
  products: Product[],
  materialsMap: Record<string, Material>,
): IFutureOrder => {
  const { clientExposure, supplierExposure, totalExposure, monthExposure } = updateFutureOrderValueCalendars(
    futureOrder,
    products,
    materialsMap,
  );

  return {
    ...futureOrder,
    clientExposure,
    supplierExposure,
    totalExposure,
    monthExposure,
  };
};

export const getFutureOrderWithUpdateAdjustments = (
  futureOrder: IFutureOrder,
  paymentAdj?: string,
  supplierAdj?: string,
  leadTime?: number,
): IFutureOrder => {
  const { supplierExposure, clientExposure } = futureOrder;

  const newSupplierExposure = supplierExposure.map((item) => ({
    ...item,
    adjustment: supplierAdj || item.adjustment,
    supplyLeadTime: leadTime === undefined ? item.supplyLeadTime : leadTime,
  }));
  const newClientExposure = paymentAdj
    ? Object.fromEntries(
        Object.entries(clientExposure).map(([key, exposure]) => [key, { ...exposure, adjustment: paymentAdj }]),
      )
    : clientExposure;

  return { ...futureOrder, clientExposure: newClientExposure, supplierExposure: newSupplierExposure };
};
